/**
 * The big instruction sign shown while navigating.
 *
 * Layout, top to bottom:
 *   ┌───────────────────────────────────────────┐
 *   │  ON  [304]              →  [290]          │  road strip: where you are,
 *   ├───────────────────────────────────────────┤  where you're going next
 *   │  ⤴   400 m                        then ↰  │
 *   │      Route 290                            │
 *   ├───────────────────────────────────────────┤
 *   │  ⚠ DO NOT STAY IN THE LEFT LANE           │  only when it's a trap
 *   ├───────────────────────────────────────────┤
 *   │  [↰][↑][↑ ↱][↱]                           │  lane bar
 *   └───────────────────────────────────────────┘
 *
 * Two requirements drive this shape:
 *
 *  - "I always want to see which road number we are on and which is the next
 *    road we turn onto." Hence the permanent road strip: it does not wait for
 *    a maneuver, it is always showing the current route number.
 *  - "No signs can ever be in Thai if English is selected - rather just road
 *    number." Every string here goes through the script guard in nav/roads.ts,
 *    which substitutes the shield number when the OSM name is unreadable.
 *
 * Imminence model: inside a speed-scaled threshold the card grows, the accent
 * animates and the arrow pulses. The lane bar flashes only when a lane change
 * is genuinely required.
 */

import type { NavProgress } from '../core/types';
import type { UnitSystem } from '../core/settings';
import { formatDistance } from '../core/format';
import { arrowSvg, glyphForManeuver, laneChipSvg } from './arrows';
import { getLang, t } from '../core/i18n';
import {
  primaryRef,
  roadNumberRefs,
  roadStripLabel,
  roadStripParts,
  safeBannerText,
} from '../nav/roads';
import { tierForStep, type RoadTier } from '../nav/paceModel';
import { uturnSign } from '../nav/turnSide';
import { forkAction, forkSentence, laneMoveSentence, laneSentence } from '../nav/phrasing';
import {
  compoundUturnSide,
  isUturnStep,
  maneuverStructure,
  uturnStructure,
} from '../nav/uturnStructure';
import { uturnGuideSvg } from './uturnGuide';
import { underBridgeTurnGuideSvg } from './structureGuide';
import { bridgeCutawaySvg, uturnBridgeCutawaySvg } from './bridgeCutaway';
import { JunctionGuide } from './junctionGuide';

/**
 * Show the follow-up maneuver when the next turn comes within this distance of
 * the current one. Measured turn-to-turn, so it reflects "another turn is
 * coming soon after this one" rather than "you are nearly at this turn".
 */
const THEN_RANGE_M = 5_000;

export class ManeuverCard {
  readonly el: HTMLElement;
  /** The detached "then" chip under the bar - see the constructor. */
  readonly signEl: HTMLElement;
  /** Car-only lane arrows, kept on the map below the vehicle marker. */
  readonly laneEl: HTMLElement;
  private detachThen = false;
  private lastRenderKey = '';
  private junctionGuide: JunctionGuide;
  /** Which maneuver the flash stages below belong to. */
  private flashKey = '';
  /** Stages already fired for that maneuver ('far' = 1 km, 'near' = 200 m). */
  private flashFired = new Set<string>();
  private flashTimer: number | null = null;

  constructor(
    parent: HTMLElement,
    /** Fired with each flash stage - main.ts sounds the triple beep here. */
    private onAlert?: () => void,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'maneuver-card';
    this.el.hidden = true;
    parent.appendChild(this.el);

    /*
     * The "then" chip - the follow-up maneuver - as a separate element under
     * the bar rather than inside it.
     *
     * It cannot live in the card's own markup and still sit below the bar: the
     * card is `overflow: hidden` and rewrites its innerHTML on every render,
     * so a child could neither escape the rounded box nor survive. A sibling
     * positioned by CSS keeps the card's render path untouched while updating
     * in step with it. Same class as the inline version, so one set of styles
     * covers both.
     */
    this.signEl = document.createElement('div');
    this.signEl.className = 'maneuver-card__then maneuver-card__then--detached';
    this.signEl.hidden = true;
    parent.appendChild(this.signEl);

    this.laneEl = document.createElement('div');
    this.laneEl.className = 'lane-guidance-detached';
    this.laneEl.hidden = true;
    parent.appendChild(this.laneEl);
    this.junctionGuide = new JunctionGuide(parent);
  }

  /**
   * Render the "then" chip outside the card (the /car layout) rather than
   * inline beside the distance. Set once at construction by main.ts.
   */
  setDetachedThen(on: boolean): void {
    this.detachThen = on;
    if (!on) {
      this.signEl.hidden = true;
      this.laneEl.hidden = true;
    }
    this.lastRenderKey = ''; // force a redraw into the other arrangement
  }

  hide(): void {
    this.junctionGuide.hide();
    this.el.hidden = true;
    this.signEl.hidden = true;
    this.laneEl.hidden = true;
    this.lastRenderKey = '';
    this.flashKey = '';
    this.flashFired.clear();
    this.el.classList.remove('is-flashing');
  }

  /*
   * Attention stages, per the driver's spec: "the flashing should be 1 km
   * before and 200 m before the event. Add one quick beep beep beep as well."
   * Each stage restarts the pulse animation (8 one-second cycles, defined on
   * .is-flashing) and sounds the beep once. Keyed to the maneuver's own
   * coordinates so a reroute or the next turn re-arms both stages.
   */
  private updateFlash(progress: NavProgress): void {
    const forkIsPrimary =
      !!progress.fork &&
      (!progress.divergence || progress.fork.distance <= progress.divergence.distance);
    const m = (forkIsPrimary ? progress.fork!.step : progress.maneuverStep)?.maneuver;
    if (!m) return;
    const key = `${forkIsPrimary ? `fork:${progress.fork!.stepIndex}` : 'turn'}:${m.location?.[0] ?? ''},${m.location?.[1] ?? ''}:${m.type}:${m.modifier ?? ''}`;
    if (key !== this.flashKey) {
      this.flashKey = key;
      this.flashFired.clear();
    }

    const d = forkIsPrimary ? progress.fork!.distance : progress.distanceToManeuver;
    let stage: 'far' | 'near' | null = null;
    if (d <= 200) {
      // Entering at 150 m arms only 'near'; 'far' is marked moot, not fired.
      if (!this.flashFired.has('near')) stage = 'near';
      this.flashFired.add('far');
      this.flashFired.add('near');
    } else if (d <= 1000 && !this.flashFired.has('far')) {
      stage = 'far';
      this.flashFired.add('far');
    }
    if (!stage) return;

    // Remove + reflow + re-add restarts the CSS animation from cycle one.
    this.el.classList.remove('is-flashing');
    void this.el.offsetWidth;
    this.el.classList.add('is-flashing');
    if (this.flashTimer !== null) clearTimeout(this.flashTimer);
    this.flashTimer = window.setTimeout(() => this.el.classList.remove('is-flashing'), 8400);

    this.onAlert?.();
  }

  update(progress: NavProgress, units: UnitSystem, speedMs: number): void {
    this.junctionGuide.update(progress, units, speedMs);
    const lang = getLang();
    const {
      step,
      approachStep,
      maneuverStep,
      banner,
      lanes,
      laneAdvice,
      distanceToManeuver,
      drivingSide,
    } = progress;

    // The nearest decision owns the main sign. In the reported failure the
    // driver had to keep left in 290 m, but a turn 2.1 km away still occupied
    // the arrow and distance while the fork was reduced to a quiet footer.
    const fork = progress.fork;
    const div = progress.divergence;
    const forkIsPrimary = !!fork && (!div || fork.distance <= div.distance);
    const activeStep = forkIsPrimary ? fork.step : maneuverStep;
    const activeBanner = forkIsPrimary ? (fork.banner ?? null) : banner;
    const activeDistance = forkIsPrimary ? fork.distance : distanceToManeuver;
    const followStep = forkIsPrimary ? maneuverStep : progress.nextStep;

    // Before the render dirty-check: distance moves every fix while the
    // rendered card often does not, and the flash stages live on the distance.
    this.updateFlash(progress);

    // Imminent when within ~12 s of travel or 80 m, whichever is larger.
    const imminentAt = Math.max(80, speedMs * 12);
    const imminent = activeDistance <= imminentAt;

    const m = activeStep?.maneuver;
    const mappedModifier = activeBanner?.primary.modifier ?? m?.modifier;
    const compoundSide = forkIsPrimary
      ? null
      : compoundUturnSide(activeStep, followStep, approachStep);
    // Only a joined pair that returns to this road is one U-turn. Two ordinary
    // same-side turns must retain their own icons and "then" warning.
    const modifier = compoundSide ? 'uturn' : mappedModifier;
    // For a U-turn, measure which way the route really swings rather than
    // inferring it from the driving side - see nav/turnSide.ts.
    const swing = compoundSide === 'left'
      ? -1
      : compoundSide === 'right'
        ? 1
        : (modifier ?? '').toLowerCase() === 'uturn'
          ? uturnSign(step, activeStep)
          : 0;
    const glyph = glyphForManeuver(
      activeBanner?.primary.type ?? m?.type ?? 'continue',
      modifier,
      drivingSide,
      swing,
    );
    const explicitUturn = !forkIsPrimary && isUturnStep(activeStep);
    const turnKind = !forkIsPrimary ? maneuverStructure(activeStep) : 'unknown';
    const uturnKind = explicitUturn
      ? uturnStructure(activeStep)
      : compoundSide && turnKind !== 'unknown'
        ? turnKind
        : 'unknown';
    const isUturn = explicitUturn || compoundSide !== null;
    // Never fall back to the generic curled arrow for a U-turn. Ordinary road
    // level is the visual baseline; explicit/OSM structure data upgrades it to
    // under-bridge or bridge. The raw kind remains in the render key below so
    // an asynchronous structure lookup redraws the card immediately.
    const visualUturnKind = isUturn && uturnKind === 'unknown' ? 'road' : uturnKind;
    const hasUturnGuide = isUturn;
    const underBridgeTurnSide: 'left' | 'right' | null =
      !hasUturnGuide &&
      turnKind === 'under-bridge' &&
      (modifier ?? '').toLowerCase().includes('left')
        ? 'left'
        : !hasUturnGuide &&
            turnKind === 'under-bridge' &&
            (modifier ?? '').toLowerCase().includes('right')
          ? 'right'
          : null;
    const hasStructureGuide = hasUturnGuide || underBridgeTurnSide !== null;
    const underBridgeTurnText = underBridgeTurnSide
      ? t(`nav.turn.underBridge.${underBridgeTurnSide}`)
      : '';

    // Road strip: only the number under our wheels and the number we join.
    // An unnumbered road intentionally leaves this strip blank.
    const on = roadStripParts(step, lang);
    const onRoad = on.label;
    const displayStep = compoundSide && followStep ? followStep : activeStep;
    const displayBanner = compoundSide ? null : activeBanner;
    const toRoad = roadStripLabel(displayStep, lang, displayBanner);
    const onRef = primaryRef(step);
    const toRef = primaryRef(displayStep, displayBanner);
    const onTier = step ? tierForStep(step) : 'single';
    const toTier = displayStep ? tierForStep(displayStep) : 'single';

    // Headline: prefer readable banner text, else the road we're joining.
    const primaryText = forkIsPrimary
      ? forkAction(fork, lang)
      : isUturn
        ? ''
        : underBridgeTurnText || safeBannerText(activeBanner, activeStep, lang) || toRoad;
    const secondaryText = forkIsPrimary ? '' : exitLabel(activeStep, lang);
    const distText = formatDistance(activeDistance, units);

    /*
     * The follow-up maneuver is shown whenever it lands within THEN_RANGE_M of
     * this one - the gap between the two turns, not how close we are to the
     * first.
     *
     * It used to appear only inside the last 500 m of the current turn, which
     * on a long step meant it flashed up for a few seconds at the very moment
     * the driver was busiest. Keying it to the gap instead means a turn that
     * genuinely has another one soon after it carries the warning for the whole
     * approach, and a turn with 40 km of open road after it never shows one.
     */
    const next = compoundSide ? null : followStep;
    const gapToNext = forkIsPrimary
      ? Math.max(0, distanceToManeuver - fork!.distance)
      : maneuverStep?.distance ?? Infinity;
    const thenGlyph =
      next && gapToNext < THEN_RANGE_M
        ? glyphForManeuver(next.maneuver.type, next.maneuver.modifier, drivingSide)
        : null;
    /*
     * How far AFTER this turn the next one comes - "100 m or 100 km" is the
     * difference between finishing this turn already in the correct lane and
     * having half an hour to think about it. The gap is the length of the step
     * the current maneuver starts, which is maneuverStep.distance.
     */
    const thenDist = thenGlyph ? formatDistance(gapToNext, units) : '';

    // The branch ahead. Always drawn while in range, whatever the countdown is
    // pointing at - the split in the carriageway is a decision the driver makes
    // long before the turn 8 km later. See nav/forks.ts.
    const forkText = forkIsPrimary ? '' : forkSentence(fork, lang);
    const forkDist = fork ? formatDistance(fork.distance, units) : '';

    /*
     * The NEAREST junction owns the lane bar.
     *
     * A fork with lane data is always closer than the maneuver being counted
     * down to - that is what makes it a fork rather than the target - so its
     * lanes are the ones the driver is about to drive through. Showing the
     * distant turn's lanes underneath a fork strip would put two different
     * junctions on the same sign with nothing to say which was which.
     */
    /*
     * A non-maneuver split outranks both. It is the nearest decision by
     * definition - it sits between here and the fork or turn - and it is the
     * only one the driver cannot read off the road, because every lane at it
     * carries the same arrow. See nav/divergence.ts.
     */
    const divOwnsLanes = !!div?.lanes?.length && (!fork || div!.distance <= fork.distance);
    const forkOwnsLanes = !divOwnsLanes && !!fork?.lanes?.length;

    const shownLanes = divOwnsLanes ? div!.lanes : forkOwnsLanes ? fork!.lanes! : lanes;
    const shownAdvice = divOwnsLanes ? div!.advice : forkOwnsLanes ? fork!.advice : laneAdvice;
    const laneOwner = forkOwnsLanes ? fork!.step.maneuver : m;

    const trap = shownAdvice?.trap ?? null;
    const laneChangeNeeded = shownAdvice?.changeRequired === true;
    /*
     * Every constrained junction states the TARGET lane. The same rule now
     * covers ordinary turns, folded forks and non-maneuver splits; none relies
     * on an assumed current lane.
     */
    const laneWarning = laneChangeNeeded
      ? [laneMoveSentence(shownAdvice, lang), trap ? laneSentence(shownAdvice, lang) : '']
          .filter(Boolean)
          .join(' — ')
      : '';

    /*
     * AI guidance fills silence; it never argues.
     *
     * Keep deriving the deterministic sentence even though it is no longer
     * rendered as a red written warning. Navigator announces the same lane
     * instruction independently, and this flag prevents an AI row from
     * competing with that spoken guidance or the lane arrows.
     */
    const rulesSpoke = !!laneWarning;
    const aiGuide = !rulesSpoke ? (progress.aiGuide?.guide ?? '') : '';

    // Cheap dirty-check so we only touch the DOM when content changes;
    // distance ticks every fix but the rest is stable per maneuver.
    const key = [
      glyph,
      primaryText,
      secondaryText,
      distText,
      imminent,
      thenGlyph,
      thenDist,
      onRoad,
      toRoad,
      glyph,
      onRef ?? '',
      toRef ?? '',
      laneWarning,
      shownLanes ? shownLanes.map((l) => `${l.valid}:${l.active}:${l.valid_indication}:${l.indications.join('|')}`).join(',') : '',
      forkText,
      forkDist,
      forkIsPrimary,
      aiGuide,
      uturnKind,
      visualUturnKind,
      turnKind,
      underBridgeTurnSide ?? '',
    ].join('~');
    if (key === this.lastRenderKey) return;
    this.lastRenderKey = key;

    this.el.hidden = false;

    // The follow-up maneuver, as a card under the bar rather than inside it.
    if (this.detachThen) {
      this.signEl.hidden = !thenGlyph;
      if (thenGlyph) {
        const html =
          `<span>${escapeHtml(t('nav.then'))}</span>` +
          arrowSvg(thenGlyph, 'maneuver-card__then-glyph') +
          `<span class="maneuver-card__then-dist">${escapeHtml(thenDist)}</span>`;
        if (this.signEl.innerHTML !== html) this.signEl.innerHTML = html;
      }

    }

    this.el.classList.toggle('is-imminent', imminent);
    this.el.classList.toggle('is-fork-primary', forkIsPrimary);
    this.el.classList.toggle('is-uturn-specific', isUturn);
    this.el.classList.toggle('is-structure-specific', hasStructureGuide);
    this.el.classList.toggle('is-under-bridge-turn', underBridgeTurnSide !== null);
    this.el.classList.toggle('has-lane-change', laneChangeNeeded && imminent);
    this.el.classList.toggle('has-lane-trap', !!trap);

    // Keyed to whichever junction the bar is describing, so a fork's chips fall
    // back to the fork's own direction rather than the distant turn's.
    const fallbackIndication = laneIndicationFor(laneOwner?.modifier);

    if (this.detachThen) {
      const detachedLanes = laneMarkup('lane-bar--detached');
      this.laneEl.hidden = !detachedLanes;
      if (detachedLanes && this.laneEl.innerHTML !== detachedLanes) {
        this.laneEl.innerHTML = detachedLanes;
      }
    }

    this.el.innerHTML = `
      ${
        onRef || toRef
          ? `<div class="road-strip">
               <span class="road-strip__now">
                 <span class="road-strip__label">${t('sign.on')}</span>
                 ${shield(onRoad, onRef, onTier)}
               </span>
               ${
                 toRoad && toRoad !== onRoad && (!this.detachThen || toRef !== onRef)
                   ? `<span class="road-strip__arrow">${arrowSvg('right', 'road-strip__arrow-glyph')}</span>
                      <span class="road-strip__next">
                        <span class="road-strip__label">${t('sign.then')}</span>
                        ${shield(toRoad, toRef, toTier)}
                      </span>`
                   : ''
               }
             </div>`
          : ''
      }
      <div class="maneuver-card__main">
        <div class="maneuver-card__arrow">${
          (turnKind === 'under-bridge' || uturnKind === 'under-bridge') && bridgeCutawaySvg(underBridgeTurnSide ?? (swing < 0 ? 'left' : 'right'), isUturn) || (hasUturnGuide && visualUturnKind === 'bridge' ? uturnBridgeCutawaySvg(swing < 0 ? 'left' : 'right') : hasUturnGuide
            ? uturnGuideSvg(
                (visualUturnKind === 'under-bridge' ? 'road' : visualUturnKind) as Exclude<typeof visualUturnKind, 'unknown'>,
                swing || (drivingSide === 'left' ? 1 : -1),
              )
            : underBridgeTurnSide
              ? underBridgeTurnGuideSvg(underBridgeTurnSide)
            : arrowSvg(glyph, 'maneuver-card__glyph'))
        }</div>
        <div class="maneuver-card__text">
          <div class="maneuver-card__distance">${distText}</div>
          ${forkIsPrimary || underBridgeTurnText ? `<div class="maneuver-card__primary">${escapeHtml(primaryText)}</div>` : ''}
          ${secondaryText ? `<div class="maneuver-card__secondary">${escapeHtml(secondaryText)}</div>` : ''}
        </div>
        ${
          // In /car the "then" chip is rendered OUTSIDE the card instead - see
          // renderThen(). The card is overflow:hidden, so a chip that has to
          // sit below the bar cannot be one of its children.
          thenGlyph && !this.detachThen
            ? `<div class="maneuver-card__then"><span>${t('nav.then')}</span>${arrowSvg(thenGlyph, 'maneuver-card__then-glyph')}<span class="maneuver-card__then-dist">${thenDist}</span></div>`
            : ''
        }
      </div>
      ${
        forkText
          ? `<div class="fork-strip fork-strip--${fork!.side}">
               <span class="fork-strip__glyph">${arrowSvg(
                 glyphForManeuver(
                   fork!.step.maneuver.type,
                   fork!.step.maneuver.modifier,
                   drivingSide,
                 ),
                 'fork-strip__icon',
               )}</span>
               <span class="fork-strip__text">${escapeHtml(forkText)}</span>
               <span class="fork-strip__dist">${escapeHtml(forkDist)}</span>
             </div>`
          : ''
      }
      ${
        aiGuide
          ? `<div class="ai-guide"><span class="ai-guide__tag">AI</span><span>${escapeHtml(
              aiGuide,
            )}</span></div>`
          : ''
      }
      ${this.detachThen ? '' : laneMarkup()}
    `;

    /* Written lane warnings were removed from every layout: the lane sign is
     * the visual instruction and Navigator owns the matching spoken sentence.
     * Keep fork-owned arrows glued under their fork strip as before. */
    function laneMarkup(extraClass = ''): string {
      const bar = shownLanes?.length
        ? `<div class="lane-bar${forkOwnsLanes ? ' lane-bar--fork' : ''}${extraClass ? ` ${extraClass}` : ''}" role="img" aria-label="${escapeHtml(
            laneAria(shownAdvice, lang),
          )}">
             <div class="lane-sign">${shownLanes
               .map((l) =>
                 laneChipSvg(
                   l.indications,
                   l.valid_indication,
                   l.valid,
                   drivingSide,
                   fallbackIndication,
                   isUturn ? swing : 0,
                 ),
               )
               .join('')}</div>
           </div>`
        : '';
      return bar;
    }
  }
}

/**
 * Route-number badge styled like the marker actually planted beside the road.
 *
 * Thai national highways are a white plate with a dark border and black
 * numerals; the motorways (7 and 9) are a blue plate with white numerals. The
 * driver reads these at 100 km/h on the roadside, so matching them means the
 * sign in the app and the sign out of the window agree at a glance.
 *
 * Labels passed here are already route-number-only. Multiple concurrent refs
 * get one badge each; no street name or road-class fallback is rendered.
 */
function shield(label: string, ref: string | null, tier: RoadTier): string {
  if (!label) return '';
  if (!ref) return '';

  const kind = tier === 'motorway' ? 'motorway' : 'highway';
  return label
    .split('/')
    .map((value) => value.trim())
    .filter(Boolean)
    .map(
      (value) =>
        `<span class="shield shield--${kind} ${
          value.length > 3 ? 'shield--wide' : ''
        }">${escapeHtml(value)}</span>`,
    )
    .join('');
}

/** Exit number / destination line, script-guarded. */
function exitLabel(step: NavProgress['maneuverStep'], lang: 'en' | 'th'): string {
  if (!step) return '';
  const exits = (step.exits ?? '').trim();
  if (exits) return lang === 'th' ? `ทางออก ${exits}` : `Exit ${exits}`;
  const refs = roadNumberRefs(step);
  return refs.length > 1 ? refs.slice(1).join(' / ') : '';
}

/** Screen-reader description of the lane bar, for whichever junction owns it. */
function laneAria(advice: NavProgress['laneAdvice'], lang: 'en' | 'th'): string {
  const spoken = laneSentence(advice, lang);
  return spoken || (lang === 'th' ? 'ช่องทางเดินรถ' : 'Lane guidance');
}

/** The lane indication implied by the maneuver, used when Mapbox omits one. */
function laneIndicationFor(modifier?: string): import('../core/types').LaneIndication | undefined {
  const m = (modifier ?? '').toLowerCase();
  switch (m) {
    case 'left':
    case 'right':
    case 'slight left':
    case 'slight right':
    case 'sharp left':
    case 'sharp right':
    case 'uturn':
    case 'straight':
      return m as import('../core/types').LaneIndication;
    default:
      return undefined;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
